// Raw CSV export of a case's activity log (case page "Export Activity" →
// "Raw data"). The readable export is built server-side (GET /activity/export).
// Every field that appears on any activity becomes a column; nested
// objects/arrays are written as JSON so nothing is lost.

// Common fields first, in this order; everything else follows alphabetically.
const LEADING_COLUMNS = [
  "_id",
  "created_at",
  "activity_type",
  "user_id",
  "user_name",
  "case_id",
  "status",
];

const cellValue = (value: any): string => {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const escapeCell = (text: string): string =>
  /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;

export const activitiesToCsv = (
  activities: any[],
  userNames: Record<string, string> = {},
): string => {
  const rows = activities.map((a) =>
    a.user_id ? { ...a, user_name: userNames[a.user_id] ?? "" } : a,
  );
  const keys = new Set<string>();
  rows.forEach((row) => Object.keys(row).forEach((k) => keys.add(k)));
  const columns = [
    ...LEADING_COLUMNS.filter((k) => keys.has(k)),
    ...Array.from(keys)
      .filter((k) => !LEADING_COLUMNS.includes(k))
      .sort(),
  ];
  const lines = [columns.map(escapeCell).join(",")];
  rows.forEach((row) =>
    lines.push(columns.map((c) => escapeCell(cellValue(row[c]))).join(",")),
  );
  return lines.join("\r\n");
};

export const activityCsvFilename = (
  firmName: string,
  date = new Date(),
  suffix = "",
) => {
  const safe =
    (firmName || "case").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_|_$/g, "") ||
    "case";
  const pad = (n: number) => String(n).padStart(2, "0");
  const stamp = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return `${safe}_activity${suffix ? `_${suffix}` : ""}_${stamp}.csv`;
};

export const downloadCsv = (csv: string, filename: string) => {
  // BOM so Excel opens UTF-8 (accented names, smart quotes) correctly.
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
